import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, ShieldCheck, Info } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetBody,
  SheetFooter,
  SheetClose,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { useSaveCredencial } from '@/lib/hooks/cofre';

// ─── Portais conhecidos ───────────────────────────────────────────────────────

const PORTAIS_CONHECIDOS = [
  { value: 'SUZANO', label: 'Portal Suzano-SP' },
  { value: 'OUTRO', label: 'Outro (digitar)' },
] as const;

// ─── Schema ───────────────────────────────────────────────────────────────────

const schema = z.object({
  portalSelect: z.string().min(1, 'Selecione o portal'),
  portalCustom: z.string().optional(),
  usuario: z.string().min(1, 'Obrigatório').max(200, 'Máximo 200 caracteres'),
  senha: z.string().min(1, 'Obrigatório').max(500, 'Máximo 500 caracteres'),
  userPassword: z.string().min(6, 'Mínimo 6 caracteres').max(200, 'Máximo 200 caracteres'),
}).superRefine((data, ctx) => {
  if (data.portalSelect === 'OUTRO' && (!data.portalCustom || data.portalCustom.trim().length < 2)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Informe o nome do portal',
      path: ['portalCustom'],
    });
  }
});

type FormValues = z.infer<typeof schema>;

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  workspaceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CofreDrawer({ workspaceId, open, onOpenChange }: Props) {
  const [showSenha, setShowSenha] = useState(false);
  const [showUserPassword, setShowUserPassword] = useState(false);

  const saveMut = useSaveCredencial(workspaceId);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      portalSelect: 'SUZANO',
      portalCustom: '',
      usuario: '',
      senha: '',
      userPassword: '',
    },
  });

  const portalSelect = watch('portalSelect');

  useEffect(() => {
    if (!open) {
      reset();
      setShowSenha(false);
      setShowUserPassword(false);
    }
  }, [open, reset]);

  async function onSubmit(values: FormValues) {
    const portal =
      values.portalSelect === 'OUTRO'
        ? (values.portalCustom ?? '').trim()
        : values.portalSelect;

    try {
      await saveMut.mutateAsync({
        portal,
        usuario: values.usuario,
        senha: values.senha,
        userPassword: values.userPassword,
      });
      toast.success('Credencial salva no cofre.');
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Falha ao salvar credencial.';
      // Senha incorreta do GestAKI — mensagem amigável
      if (msg.toLowerCase().includes('incorreta') || msg.includes('403')) {
        toast.error('Senha do GestAKI incorreta. Tente novamente.');
      } else {
        toast.error(msg);
      }
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
          <SheetHeader
            eyebrow="Cofre de credenciais"
            title="Nova credencial"
            description="As credenciais ficam cifradas. Somente você pode decifrá-las."
          />

          <SheetBody className="space-y-5">
            {/* Aviso zero-knowledge */}
            <div className="flex items-start gap-2 rounded-2 border border-info/30 bg-info-soft/30 p-3">
              <Info className="h-4 w-4 text-info shrink-0 mt-0.5" strokeWidth={1.8} />
              <p className="text-xs text-fg-muted leading-relaxed">
                Sua senha do GestAKI é usada localmente para cifrar a credencial do portal — ela
                não sai do servidor em texto claro. Sem ela, ninguém consegue revelar a senha
                salva, nem nós.
              </p>
            </div>

            {/* Portal */}
            <div>
              <Label htmlFor="portalSelect" className="mb-1.5 block">
                Portal
              </Label>
              <select
                id="portalSelect"
                className="flex h-9 w-full rounded-2 border border-card-stroke bg-card px-3 py-1 text-sm text-fg shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-mint-deep disabled:cursor-not-allowed disabled:opacity-50"
                {...register('portalSelect')}
              >
                {PORTAIS_CONHECIDOS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </select>
              {errors.portalSelect && (
                <p className="text-[11px] text-warn mt-1">{errors.portalSelect.message}</p>
              )}
            </div>

            {portalSelect === 'OUTRO' && (
              <div>
                <Label htmlFor="portalCustom" className="mb-1.5 block">
                  Nome do portal
                </Label>
                <Input
                  id="portalCustom"
                  placeholder="Ex.: Portal do Estado SP"
                  autoComplete="off"
                  {...register('portalCustom')}
                />
                {errors.portalCustom && (
                  <p className="text-[11px] text-warn mt-1">{errors.portalCustom.message}</p>
                )}
              </div>
            )}

            {/* Usuário do portal */}
            <div>
              <Label htmlFor="usuario" className="mb-1.5 block">
                Usuário do portal
              </Label>
              <Input
                id="usuario"
                placeholder="CPF, e-mail ou login"
                autoComplete="username"
                {...register('usuario')}
              />
              {errors.usuario && (
                <p className="text-[11px] text-warn mt-1">{errors.usuario.message}</p>
              )}
            </div>

            {/* Senha do portal */}
            <div>
              <Label htmlFor="senha" className="mb-1.5 block">
                Senha do portal
              </Label>
              <div className="relative">
                <Input
                  id="senha"
                  type={showSenha ? 'text' : 'password'}
                  placeholder="Senha do portal a ser guardada"
                  autoComplete="new-password"
                  className="pr-10"
                  {...register('senha')}
                />
                <button
                  type="button"
                  aria-label={showSenha ? 'Ocultar senha do portal' : 'Mostrar senha do portal'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 grid h-7 w-7 place-items-center rounded-1 text-fg-dim hover:text-fg transition-colors"
                  onClick={() => setShowSenha((v) => !v)}
                >
                  {showSenha ? (
                    <EyeOff className="h-4 w-4" strokeWidth={1.7} />
                  ) : (
                    <Eye className="h-4 w-4" strokeWidth={1.7} />
                  )}
                </button>
              </div>
              {errors.senha && (
                <p className="text-[11px] text-warn mt-1">{errors.senha.message}</p>
              )}
            </div>

            {/* Divisor */}
            <div className="pt-1 border-t border-card-stroke">
              <div className="flex items-center gap-2 mb-3">
                <ShieldCheck className="h-4 w-4 text-mint-deep" strokeWidth={1.8} />
                <span className="text-sm font-medium text-fg">Confirme sua identidade</span>
              </div>
            </div>

            {/* Senha do GestAKI */}
            <div>
              <Label htmlFor="userPassword" className="mb-1.5 block">
                Sua senha do GestAKI
              </Label>
              <div className="relative">
                <Input
                  id="userPassword"
                  type={showUserPassword ? 'text' : 'password'}
                  placeholder="Sua senha de acesso ao GestAKI"
                  autoComplete="current-password"
                  className="pr-10"
                  {...register('userPassword')}
                />
                <button
                  type="button"
                  aria-label={showUserPassword ? 'Ocultar senha GestAKI' : 'Mostrar senha GestAKI'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 grid h-7 w-7 place-items-center rounded-1 text-fg-dim hover:text-fg transition-colors"
                  onClick={() => setShowUserPassword((v) => !v)}
                >
                  {showUserPassword ? (
                    <EyeOff className="h-4 w-4" strokeWidth={1.7} />
                  ) : (
                    <Eye className="h-4 w-4" strokeWidth={1.7} />
                  )}
                </button>
              </div>
              <p className="text-[11px] text-fg-muted mt-1">
                Usada para derivar a chave de cifração — nunca armazenada em texto claro.
              </p>
              {errors.userPassword && (
                <p className="text-[11px] text-warn mt-1">{errors.userPassword.message}</p>
              )}
            </div>
          </SheetBody>

          <SheetFooter>
            <SheetClose asChild>
              <Button variant="ghost" type="button">
                Cancelar
              </Button>
            </SheetClose>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.8} />
                  Salvando…
                </>
              ) : (
                'Salvar no cofre'
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
